/**
 * iter845c — POS checkout hook. Encapsulates the entire order-creation
 * lifecycle previously inlined as `handlePayment` in CashierPOSPage.jsx:
 *
 *   1. Validate cart + credit-customer requirement
 *   2. POST /cashier/orders → creates the order in the ledger
 *   3. Toast success + low-stock warnings (iter767)
 *   4. Reset cart state
 *   5. Broadcast "paid" state to the Customer Display window (iter700)
 *   6. Non-print bill delivery: WhatsApp / Email / Go Green (iter746)
 *   7. Auto-print via Bridge (receipt) + fan-out prep tickets (iter606)
 *   8. ePOS-Direct silent path for iPad-only branches (iter669)
 *   9. iPrint Share Sheet auto-open (iter667)
 *
 * Pure lift-and-shift — zero behaviour changes. Every localStorage flag,
 * debug branch, and toast message preserved identically.
 */
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { pickPrinter, printViaIPrintShare } from '@/lib/printBridge';
import { printViaEposDirect } from '@/lib/eposPrint';
import { crumb, sendFailureTelemetry } from '@/lib/posBreadcrumbs';
import { promptApproval } from '@/lib/posDiscount';

// Cash (or a split with a cash part) opens the drawer exactly once per saved order.
const paidWithCash = (o) => String(o?.payment_method || '').toLowerCase() === 'cash'
  || (o?.payment_details || []).some((p) => String(p?.mode || p?.method || '').toLowerCase() === 'cash' && Number(p?.amount) > 0);
const _drawerOpenedFor = new Set();
const claimCashDrawer = (o) => {
  if (!o?.id || !paidWithCash(o) || _drawerOpenedFor.has(o.id)) return false;
  _drawerOpenedFor.add(o.id);
  return true;
};
const kickDrawerOnly = (p) => {
  if (!p || !(p.ip_address || p.connection_type === 'sunmi')) return;
  import('@/lib/nativeThermalPrint').then(({ kickNativeDrawer }) =>
    kickNativeDrawer({ type: p.connection_type, address: p.ip_address, port: p.tcp_port || p.port || 9100 })).catch(() => {});
};

export function usePosCheckout({
  // Cart + order state (reads)
  cart,
  total,
  paymentMethod,
  splitPayments,
  selectedCustomer,
  discount,
  discountType,
  orderType,
  tableNumber,
  scheduledFor,
  setScheduledFor,
  notes,
  user,
  branchId,
  printers,
  // iter916 — Tailor measurements metadata (optional)
  saleMetadata,
  setSaleMetadata,
  // Fire new paid orders to the KDS (branch has a kitchen)
  autoKitchen,
  // Opens the masked manager-approval dialog (resolves {pin, reason} | null)
  requestApproval,
  // Waiter/Table — id of the open check being finalized (null for walk-in)
  activeCheckId,
  setActiveCheckId,
  // Setters
  setLastOrder,
  setCart,
  setDiscount,
  setSelectedCustomer,
  setNotes,
  setTableNumber,
  setGoGreen,
  setShowPayment,
  setShowReceipt,
  setShowCustomerSelect,
  setPrinters,
  // Data refresh
  fetchData,
  fetchOrders,
  // Print helpers from usePosPrinting
  buildPrintPayload,
  printOrderViaBridge,
  printPrepTickets,
}) {
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(null);
  const handlePaymentRef = useRef(null);
  const handlePayment = useCallback(async (splitData, approvals = {}) => {
    crumb('checkout-start', {
      cart_len: cart.length,
      total,
      method: paymentMethod,
      split: !!splitData?.payment_details?.length,
      bill_delivery: splitData?.bill_delivery || 'print',
    });
    if (cart.length === 0) {
      crumb('checkout-abort', { reason: 'empty-cart' });
      toast.error('Cart is empty');
      return;
    }

    // Derive the payment_details array. If the cashier used the
    // split-payment dialog we trust THAT shape directly; otherwise we
    // build a single-row array from the chosen `paymentMethod`.
    const details = splitData?.payment_details && splitData.payment_details.length > 0
      ? splitData.payment_details
      : (paymentMethod === 'split' && splitPayments.length > 0
          ? splitPayments
          : [{ mode: paymentMethod, amount: total }]);

    // For credit-flagged details (no cash/bank component), customer is required.
    const hasCredit = details.some(d => d.mode === 'credit');
    if (hasCredit && !selectedCustomer) {
      crumb('checkout-abort', { reason: 'credit-without-customer' });
      toast.error('Please select a customer for credit payment');
      setShowCustomerSelect(true);
      return;
    }

    // Derive headline payment_method for receipt / reporting. If multiple
    // methods → 'split'. Else use the single method.
    const headline = details.length > 1 ? 'split' : (details[0]?.mode || paymentMethod);

    // Phase A.1 (C) — the sale belongs to the branch where the POS is
    // actually operating (the validated active POS branch), NOT the cashier's
    // home branch and NEVER a silent 'default'. Fail clearly if unresolved so
    // we never create a wrongly-attributed sale. The backend additionally
    // runs resolve_safe_branch_id to block any branch the user can't write to,
    // and uses this SAME branch for pricing/accounting/inventory/ZATCA.
    if (!branchId) {
      crumb('checkout-abort', { reason: 'no-branch' });
      toast.error('No active branch selected — pick a branch before completing the sale.');
      return;
    }

    // Tap guard: one checkout in flight at a time; one request id per checkout attempt.
    if (inFlightRef.current) { crumb('checkout-abort', { reason: 'in-flight' }); return; }
    inFlightRef.current = true;
    if (!requestIdRef.current) requestIdRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      const orderData = {
        client_request_id: requestIdRef.current,
        branch_id: branchId,
        customer_id: selectedCustomer?.id || null,
        items: cart.map(c => ({
          item_id: c.item_id,
          quantity: c.quantity,
          modifiers: c.modifiers,
        })),
        discount,
        discount_type: discountType,
        ...approvals,
        payment_method: headline,
        payment_details: details,
        change_due: splitData?.change_due || 0,
        order_type: orderType,
        table_number: tableNumber || null,
        scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        // Waiter/Table — when finalizing an existing open check, pass its id so
        // the canonical endpoint UPDATES that same pos_order (idempotent) rather
        // than creating a copy. Normal walk-in sales leave this null.
        order_id: activeCheckId || null,
        notes,
        terminal_payment: splitData?.terminal_payment || null,
        // iter916 — tailor measurements + reference photo (pos_orders.metadata)
        metadata: saleMetadata || null,
      };

      const { data } = await api.post('/cashier/orders', orderData, { headers });
      setLastOrder(data);
      // KDS — reuse the canonical send-kitchen fire (server skips checks already fired → no duplicate ticket).
      if (autoKitchen && !data.idempotent) {
        // One retry (server is idempotent → no duplicate ticket); then warn so a paid order is never silently missed.
        const fire = () => api.post(`/cashier/orders/${data.id}/send-kitchen`, {}, { headers });
        fire().catch(() => new Promise((r) => setTimeout(r, 2000)).then(fire)).catch(() => {
          crumb('kds-fire-failed');
          toast.warning(`Order #${data.order_number} did not reach the kitchen — tap "Send to Kitchen"`, { duration: 15000 });
        });
      }
      // Phase A.1 — refresh Today's Orders so the completed sale appears
      // immediately without a logout/reload (reuses the existing fetcher).
      try { fetchOrders?.(); } catch (_e) { /* non-fatal */ }
      crumb('order-created', {
        order_number: data.order_number,
        order_id: data.id,
        total: data.total,
        payment_method: data.payment_method,
        items: data.items?.length,
      });
      toast.success(`Order #${data.order_number} created!`);

      // iter767 — Low-stock alert: warn the cashier when this sale pushed any
      // recipe ingredient at/below its minimum level.
      if (Array.isArray(data.stock_warnings) && data.stock_warnings.length > 0) {
        data.stock_warnings.forEach((w) => {
          const msg = w.out_of_stock
            ? `${w.item_name} is OUT OF STOCK (${w.balance} ${w.unit})`
            : `Low stock: ${w.item_name} — ${w.balance} ${w.unit} left (min ${w.min_level})`;
          toast.warning(msg, { duration: 6000 });
        });
      }

      // Reset cart
      setCart([]);
      setDiscount(0);
      setSelectedCustomer(null);
      setNotes('');
      setTableNumber('');
      setScheduledFor?.('');
      setGoGreen?.(false);
      setSaleMetadata?.(null);
      setActiveCheckId?.(null);
      setShowPayment(false);
      crumb('cart-reset');

      // iter668 — When auto-iPrint is ON the cashier wants the Share
      // Sheet immediately, not a dialog blocking the screen. Skip the
      // OrderCompleteDialog entirely in that mode; the Share Sheet IS
      // the success state. The dialog still opens for everyone else.
      // iter669 — Also skip when ePOS-Print Direct is enabled: the
      // receipt has already been fired silently.
      const autoIPrintOn = (typeof window !== 'undefined' && localStorage.getItem('auto_iprint_on_pay') === '1');
      const eposDirectFlag = (typeof window !== 'undefined' && localStorage.getItem('epos_direct_on_pay') === '1');
      // iter833 — Master "Auto-print receipt" toggle in POS Settings.
      const autoPrintFlag = (typeof window !== 'undefined' && localStorage.getItem('auto_print_on_pay') !== '0');
      const iosShareReady = typeof navigator !== 'undefined' && !!navigator.share;
      // iter746 — Bill output chosen at checkout (print / whatsapp / email / go_green)
      const billDelivery = splitData?.bill_delivery || 'print';
      const billContact = splitData?.contact || {};
      const skipReceiptDialog = (autoIPrintOn && iosShareReady) || eposDirectFlag || billDelivery !== 'print' || (autoPrintFlag && billDelivery === 'print');
      if (!skipReceiptDialog) {
        setShowReceipt(true);
      }

      // iter700 — Tell the Customer Display window the order is closed
      // so it can flip to the green "Thank you" screen. It auto-clears
      // back to idle once the cashier starts the next order (cart length
      // goes back to >0, status flips to 'active').
      try {
        const paidSnapshot = {
          status: 'paid',
          orderNo: data?.order_number || data?.order_id || '',
          cart: [], subtotal: 0, tax: 0, total: 0, discount: 0,
          branch: user?.branch_name || '',
          ts: Date.now(),
        };
        localStorage.setItem('cd_state_v1', JSON.stringify(paidSnapshot));
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel('smarto_customer_display');
          bc.postMessage(paidSnapshot);
          bc.close();
        }
      } catch { /* ignored */ }

      // Refresh stats
      fetchData();

      // iter746 — Non-print bill delivery (WhatsApp / Email / Go Green).
      // Skip ALL printing paths below when the cashier didn't choose Print.
      const openDrawerNow = claimCashDrawer(data);
      if (billDelivery !== 'print') {
        crumb('bill-delivery-alt', { channel: billDelivery });
        if (openDrawerNow) {
          let ps = printers;
          if (!ps || !ps.length) {
            try {
              const tk = localStorage.getItem('cashier_token') || localStorage.getItem('token');
              ps = (await api.get('/cashier/printers', { headers: { Authorization: `Bearer ${tk}` } })).data || [];
            } catch { ps = []; }
          }
          kickDrawerOnly(pickPrinter(ps, 'receipt', user?.branch_id));
        }
        const token2 = localStorage.getItem('cashier_token');
        const h2 = { headers: { Authorization: `Bearer ${token2}` } };
        try {
          if (billDelivery === 'go_green') {
            await api.post(`/cashier/orders/${data.id}/mark-delivery`, { method: 'go_green' }, h2);
            crumb('bill-delivery-ok', { channel: 'go_green' });
            toast.success('Paperless bill saved 🌱 — no print, no send.');
          } else if (billDelivery === 'whatsapp') {
            const tId = toast.loading('Sending bill on WhatsApp…');
            await api.post(`/cashier/orders/${data.id}/send-whatsapp`, { phone: billContact.phone || '' }, h2);
            crumb('bill-delivery-ok', { channel: 'whatsapp' });
            toast.success('Bill sent on WhatsApp ✓', { id: tId });
          } else if (billDelivery === 'email') {
            const tId = toast.loading('Emailing bill…');
            await api.post(`/cashier/orders/${data.id}/send-email`, { email: billContact.email || '' }, h2);
            crumb('bill-delivery-ok', { channel: 'email' });
            toast.success('Bill emailed ✓', { id: tId });
          }
        } catch (e) {
          crumb('bill-delivery-fail', { channel: billDelivery, err: e.response?.data?.detail || String(e?.message || e) });
          toast.error(e.response?.data?.detail || `Failed to ${billDelivery === 'whatsapp' ? 'send WhatsApp' : billDelivery === 'email' ? 'send email' : 'save'} bill`);
        }
        return; // done — no printing for non-print delivery
      }

      // Auto-print via bridge — receipt always when bridge is up; prep
      // tickets fan out to EVERY kitchen + bar printer configured for
      // this branch, each receiving only the items its station routes
      // to (iter606). Non-blocking — failure leaves the preview dialog.
      // iter673 — Make printer fetch synchronous BEFORE the ePOS-Direct /
      //           iPrint branches. Previously on the first order of the
      //           session, `printers` was empty → ePOS-Direct + iPrint
      //           skipped silently. Now we always have printers ready.
      // iter674 — Debug toast: when localStorage.epos_debug='1' is set on
      //           the device, we surface a sticky info toast showing the
      //           exact print decision path.
      const eposDebug = typeof window !== 'undefined' && localStorage.getItem('epos_debug') === '1';
      const dbg = [];
      let printersForPay = printers;
      if (!printersForPay || !printersForPay.length) {
        try {
          const tk = localStorage.getItem('cashier_token') || localStorage.getItem('token');
          const { data: freshPrinters } = await api.get('/cashier/printers', { headers: { Authorization: `Bearer ${tk}` } });
          printersForPay = freshPrinters || [];
          setPrinters(printersForPay);
          if (eposDebug) dbg.push(`fetched ${printersForPay.length} printer(s)`);
        } catch (_e) {
          printersForPay = [];
          if (eposDebug) dbg.push('printer fetch FAILED');
        }
      } else if (eposDebug) {
        dbg.push(`${printersForPay.length} printer(s) cached`);
      }
      if (printersForPay && printersForPay.length) {
        const receiptP = pickPrinter(printersForPay, 'receipt', user?.branch_id);
        // iter833 — Print immediately when either the printer is flagged
        // auto_print OR the device-level "Auto-print receipt" toggle is ON.
        if (receiptP?.auto_print || autoPrintFlag) {
          crumb('print-receipt-fired', { printer: receiptP?.name || 'auto' });
          printOrderViaBridge(data, { role: 'receipt', openDrawer: openDrawerNow });
        } else if (openDrawerNow) {
          kickDrawerOnly(receiptP);
        }
        crumb('prep-tickets-fired');
        printPrepTickets(data);
      } else {
        crumb('print-skipped', { reason: 'no-printers' });
      }

      // iter669 — ePOS-Print Direct (silent, native iPad path). When the
      // cashier has opted in (Settings → Printers → "ePOS-Print Direct"),
      // we POST the receipt XML straight to the printer's built-in HTTP
      // service — no Bridge, no iPrint, no AirPrint discovery. Wins over
      // auto-iPrint when both flags are on because it's fully silent.
      const eposDirectOn = (typeof window !== 'undefined' && localStorage.getItem('epos_direct_on_pay') === '1');
      if (eposDebug) dbg.push(`ePOS toggle ${eposDirectOn ? 'ON' : 'OFF'}`);
      let eposDirectFired = false;
      if (eposDirectOn) {
        try {
          const receiptPrinter = (printersForPay || []).find((p) => (p.type || 'receipt') === 'receipt') || (printersForPay || [])[0];
          if (eposDebug) dbg.push(`printer=${receiptPrinter?.name || 'none'} ip=${receiptPrinter?.ip_address || 'none'}`);
          if (receiptPrinter?.ip_address) {
            const payload = buildPrintPayload(data, { role: 'receipt' });
            const r = await printViaEposDirect(receiptPrinter, payload);
            if (eposDebug) dbg.push(`fetch ${r.ok ? 'OK' : 'FAIL: ' + (r.error || '?')}`);
            if (r.ok) {
              eposDirectFired = true;
              if (!eposDebug) toast.success('Receipt sent (ePOS Direct)');
            } else {
              toast.error(r.msg || 'ePOS Direct failed — falling back.');
            }
          } else {
            toast.error('ePOS Direct enabled but no receipt printer with an IP was found. Go to Settings → Printers and add one.');
          }
        } catch (e) {
          if (eposDebug) dbg.push(`exception: ${String(e?.message || e)}`);
          toast.error(`ePOS Direct error: ${String(e?.message || e)}`);
        }
      }
      if (eposDebug) {
        // Sticky 8s toast so cashier has time to read + copy.
        toast.info(`[debug] ${dbg.join(' → ')}`, { duration: 8000 });
        console.log('[smarto][epos-debug]', dbg);
      }

      // iter667 — Auto-open Epson iPrint Share Sheet after payment when
      // the cashier has opted in (Settings → Printers → "Auto-iPrint on
      // payment"). Only on iPad Safari.
      try {
        const autoIPrint = localStorage.getItem('auto_iprint_on_pay') === '1';
        if (!eposDirectFired && autoIPrint && typeof navigator !== 'undefined' && navigator.share) {
          // Delay so the OrderCompleteDialog is mounted first.
          setTimeout(() => {
            (async () => {
              const receiptPrinter = (printers || []).find((p) => (p.type || 'receipt') === 'receipt') || (printers || [])[0] || { paper_width: '80mm' };
              const payload = buildPrintPayload(data, { role: 'receipt' });
              toast.info('Opening iPrint share sheet…');
              const r = await printViaIPrintShare(receiptPrinter, payload);
              if (!r.ok && r.error !== 'cancelled' && r.error !== 'web-share-unavailable') {
                toast.error(`iPrint share failed: ${r.error}`);
              }
            })();
          }, 600);
        }
      } catch (_e) { /* opt-in flag missing → silent */ }
    } catch (err) {
      const errMsg = err.response?.data?.detail || String(err?.message || err);
      crumb('checkout-fail', { err: errMsg });
      // iter845e — Fire-and-forget audit trail to backend. Non-blocking.
      sendFailureTelemetry({
        api,
        summary: `Failed to create order (cart total ${total})`,
        error: errMsg,
        branch_id: user?.branch_id || null,
      });
      // Server asks for manager approval (discount limit / cancelling fired items) → ask once and retry.
      const approved = await promptApproval(err, approvals, requestApproval);
      if (approved) {
        requestIdRef.current = null;
        if (Object.keys(approved).length) setTimeout(() => handlePaymentRef.current?.(splitData, approved), 0);
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to create order');
      // Server answered (validation etc.) → next attempt is a new request; network errors keep the id so a retry is de-duplicated.
      if (err.response) requestIdRef.current = null;
      return;
    } finally {
      inFlightRef.current = false;
    }
    requestIdRef.current = null;
  }, [
    cart, total, paymentMethod, splitPayments, selectedCustomer,
    discount, discountType, orderType, tableNumber, scheduledFor, notes, user, branchId, printers,
    setScheduledFor, setLastOrder, setCart, setDiscount, setSelectedCustomer, setNotes,
    setTableNumber, setShowPayment, setShowReceipt, setShowCustomerSelect,
    setPrinters, fetchData, fetchOrders, buildPrintPayload, printOrderViaBridge, printPrepTickets, autoKitchen, requestApproval,
  ]);

  handlePaymentRef.current = handlePayment;
  return { handlePayment };
}

export default usePosCheckout;
